import { MultipleQueriesResponse } from '@algolia/client-search';
import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import algoliasearch from 'algoliasearch';

import { useApplicationConfig } from '@bonusly/application';
import { useHttpClient } from '@bonusly/http-client';

import { SearchBonus } from '../types/bonus';

export interface SearchUser {
	display_name: string;
	email: string;
	first_name: string;
	full_pic: string;
	last_name: string;
	legal_name: string;
	objectID: string;
	profile_pic: string;
	slug: string;
}

export interface GlobalSearchResult {
	bonuses: SearchBonus[];
	users: SearchUser[];
}

export type GlobalSearchResponse = MultipleQueriesResponse<SearchBonus | SearchUser>;

enum QueryIndex {
	Bonuses = 'Bonus_production',
	Users = 'User_production',
}

export const searchQueryKey = (query: string) => ['global-search', query];

interface CancellableDelayParams {
	signal?: AbortSignal;
	delay?: number;
}

const cancellableDelay = ({ delay = 200, signal }: CancellableDelayParams = {}) => {
	return new Promise<void>((resolve, reject) => {
		const timeout = setTimeout(() => {
			resolve();
		}, delay);

		signal?.addEventListener('abort', () => {
			clearTimeout(timeout);
			reject();
		});
	});
};

export const useGlobalSearch = (query: string, options: UseQueryOptions<GlobalSearchResponse, unknown, GlobalSearchResult>) => {
	const algoliaClientId = useApplicationConfig('algoliaClientId');
	const queryClient = useQueryClient();
	const http = useHttpClient('v2');

	const params = {
		hitsPerPage: query ? 50 : 4,
		facets: ['active'],
	};

	return useQuery<GlobalSearchResponse, unknown, GlobalSearchResult>(
		searchQueryKey(query),
		async ({ signal }) => {
			await cancellableDelay({ signal });

			const client = await queryClient.fetchQuery(
				['algolia-client'],
				async () => {
					const response = await http.get<{ key: string }>('/algolia_key_generator');

					const key = response.data.key;

					return algoliasearch(algoliaClientId, key);
				},
				{
					// Key expires in 1 hour so force a refresh after 55 minutes
					staleTime: 55 * 60 * 1000,
				}
			);

			return client.multipleQueries<SearchBonus | SearchUser>([
				{
					indexName: QueryIndex.Users,
					query,
					params: {
						...params,
						facetFilters: ['active:true'],
					},
				},
				{
					indexName: QueryIndex.Bonuses,
					query,
					params,
				},
			]);
		},
		{
			select: (data) => ({
				bonuses: (data.results[1] as any).hits as SearchBonus[],
				users: (data.results[0] as any).hits as SearchUser[],
			}),
			refetchOnWindowFocus: false,
			...options,
		}
	);
};
